import styled from '@emotion/styled'
import React from 'react'

import FieldError from './FieldError'
import Fieldset from './Fieldset'
import Label from './Label'
import { formControlReset } from 'styles/reset'

type Props = JSX.IntrinsicElements['textarea'] & {
	label: string
	error?: string
	leftAdornment?: React.ReactNode
	rightAdornment?: React.ReactNode
}

const StyledTextarea = styled.textarea`
	${formControlReset}
	resize: none;
	width: 100%;
	max-width: 380px;
	padding: 12px 16px;
	color: ${({ theme }) => theme.colors.light.secondaryTextColor};
	line-height: 1.4;
	outline-color: currentColor;
	border-radius: ${({ theme }) => theme.borderRadiuses.md};
	background-color: ${({ theme }) => theme.colors.light.surfaceVariant};
	opacity: ${({ disabled }) => (disabled ? '0.55' : '1.0')};
	${({ theme }) => theme.typography.paragraph}

	&:focus {
		outline-style: solid;
		outline-width: 1px;
		outline-color: ${({ theme }) => theme.colors.light.surfaceInteractive};
	}
`

export const LabelWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: ${({ theme }) => theme.spaces.space6};
	min-height: 28px;
	max-width: 380px;
`

const Textarea = React.forwardRef<HTMLTextAreaElement, Props>(
	({ label, error, leftAdornment, rightAdornment, ...restProps }, ref) => {
		const id = React.useId()

		return (
			<Fieldset>
				<LabelWrapper>
					{leftAdornment}
					<Label htmlFor={id} required={restProps.required}>
						{label}
					</Label>
					{rightAdornment}
				</LabelWrapper>
				<StyledTextarea rows={4} {...restProps} id={id} ref={ref} />
				{error && <FieldError>{error}</FieldError>}
			</Fieldset>
		)
	},
)

Textarea.displayName = 'Textarea'

export default Textarea
